import {
  useDashboardUIActions,
  useDashboardUIContext,
} from "@contexts/dashboardUI";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { useState, useMemo, useEffect, useRef } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import LoaderItem from "./value/accordion/LoaderItem";
import CreditorAccordion, { AnnulledCreditorsSection } from "./offcanvas/contract/creditorAccordion";
import AppropriationEditor from "./details/AppropriationEditor";
import AnnulmentIndicator from "@components/titleAnnulments/AnnulmentIndicator";
import { isBuildingTrial } from "@utils/checkPermiossion";
import ContractAccordion from "./offcanvas/contract/contractAccordion";
import ResourceAccordion from "./offcanvas/resourceAccordion";
import UpdateValues from "./offcanvas/updateValues";
import Pagination from "./offcanvas/pagination";
import MainCards from "./offcanvas/mainCards";
import EapDisb from "./offcanvas/curve/eapDisb";
import EapResources from "./offcanvas/curve/eapDivisions";
import EapLevels from "./offcanvas/curve/eapLevels";
import { titleIdentifier } from "@components/dashboard/TitleIdentifier";
import { downloadCsv } from "@utils/downloadCsv";
import { trackReportDownload } from "@utils/activityTracker";
import Select from 'react-select';
import FinancialBarChart from "./financialProjection/FinancialBarChart";
import OffCanvasDateFilter from "./offcanvas/offCanvasDateFilter";
import OffCanvasGraph from "./offcanvas/offCanvasGraph";
import StockAccordion from "./offcanvas/stockAccordion";
import Card from "@components/Card";
import { formatNumberToCurrency } from "@utils/formatNumber";
import TableMonthExtraValues from "./offcanvas/tableMonthExtraValues";
import AdvancementAccordion from "./advancements/AdvancementAccordion";
import TableMonthAdvancements from "./offcanvas/tableMonthAdvancements";
import AmpliMonthDisbursementSection, { tableMonthToCompetence } from "./disbursement/AmpliMonthDisbursementSection";
import { isAmpliDisbursementPolicy } from "./financialProjection/cashFlowPlanning";
import OffcanvasLessons from "./offcanvas/lessons";
import AppropriationLevelWarning from "./offcanvas/appropriationLevelWarning";
import ZeroBudgetNotice from "./offcanvas/zeroBudgetNotice";
import PresentationMutationGate from "./PresentationMutationGate";
import { isPresentationProject } from "@utils/presentationMode";
import { presentationViewKey } from "@utils/presentationUiSafety";
import PlannedMeasuredPanel from "./offcanvas/plannedMeasured/PlannedMeasuredPanel";
import { defaultItemDetailTab } from "./offcanvas/plannedMeasured/defaultTab";
import MonthlyProjectionComparison from "./offcanvas/plannedMeasured/MonthlyProjectionComparison";
import {
  defaultFocusMonth,
  formatMonthLabel,
  monthKeyFromDate,
  monthKeyToApiMonth,
  monthKeyToDate,
} from "./offcanvas/plannedMeasured/monthlyProjectionModel";
import ResourceGroupAiPanel from "./ai/ResourceGroupAiPanel";

export const itemDetailExtraValueMutation = (
  value,
  description,
  competence = new Date().toISOString().slice(0, 7),
) => ({
  operation: "append",
  entry: {
    value,
    installments: 1,
    start_date: competence,
    end_date: competence,
    isValid: true,
    description,
  },
});

export const extraValueDocumentTotal = (document) => (
  Array.isArray(document?.history)
    ? document.history.reduce((total, entry) => (
      entry?.isValid && Number.isFinite(Number(entry.value))
        ? total + Number(entry.value)
        : total
    ), 0)
    : null
);

const RESOURCE_SEARCH_LABELS = {
  resources: "Pesquisar insumos neste grupo",
  creditors: "Pesquisar fornecedores neste grupo",
  stock: "Pesquisar itens de estoque neste grupo"
};

const VALUE_ADDED_PATHS = [
  "/dashboard/valor-agregado",
  "/dashboard/valor-agregado/:project_id",
];

// Meses civis do filtro da lateral, só para escolher o mês em foco inicial.
const monthlyMonthKeys = ({ beginAt, endAt } = {}) => {
  const begin = beginAt ? monthKeyFromDate(beginAt) : null
  const end = endAt ? monthKeyFromDate(endAt) : null
  if (!begin || !end || begin > end) return []
  const keys = []
  const cursor = monthKeyToDate(begin)
  while (monthKeyFromDate(cursor) <= end && keys.length < 1200) {
    keys.push(monthKeyFromDate(cursor))
    cursor.setUTCMonth(cursor.getUTCMonth() + 1)
  }
  return keys
}

// Contratos MEGA não têm vínculo com a linha do orçamento: a única rota que traz o
// estruturado do serviço apropriado (`CanteiroDigital/GetContratosByCodigoOrcamento`)
// recusa o usuário da integração (verificação de 07/10/2026). Em vez de "Nenhum contrato
// encontrado", a aba declara a ausência e remete à área de Pedidos e Contratos.
export const MEGA_CONTRACTS_WITHOUT_BUDGET_LINK =
  "Contratos MEGA não têm vínculo com o orçamento. Consulte a área Pedidos e Contratos.";

const isMegaErp = (erp) => String(erp || "").toUpperCase() === "MEGA";

const TabButton = (label, name) => {
  return <button onClick={() => setTabView(name)} className={tabView === name ? 'active' : undefined}>{label}</button>
}
const ItemDetailOffcanvas = () => {
  const { selectedDetail: { item, type }, activeProject, companies, resourceEAP, projectionTable, tableMonthSelected, scheduleAnalysis, graphData, offCanvasFilterDates } = useProjectContext();
  const { getResources, getCreditors, getContracts, getExtraValues, getWbsPath, updateExtra, updateStock, updateProjectLive, getSingleFollowUps, getStock, getScheduleAnalysis, selectDetail, selectProject, changeGraphDates } = useProjectActions();
  const { showItemDetailOffcanvas, barChartDate } = useDashboardUIContext();
  const { toggleItemDetailOffcanvas, closeItemDetailOffcanvas } = useDashboardUIActions();
  const location = useLocation();
  const navigate = useNavigate();
  // Modo Ampli: a seção de proveniência do mês substitui "Antecipações"; a
  // competência da tabela ("MM/AAAA") vira "AAAA-MM" num helper puro — malformada
  // devolve null e a seção não renderiza nem busca.
  const ampliMode = isAmpliDisbursementPolicy(activeProject?.cashFlowPlanning);
  const megaProject = isMegaErp(
    (Array.isArray(companies) ? companies : []).find((company) => company?._id === activeProject?.company_id)?.erp,
  );
  const monthCompetence = tableMonthToCompetence(tableMonthSelected);
  /**
   * "Ajustar regra" (spec 8.4): a regra NÃO é editada aqui dentro — o editor mora no
   * módulo de Desembolso, com o picker de alvo, a pré-visualização e a gravação. O
   * botão navega para lá levando a chave do alvo em `?editRule=`, e a página abre o
   * editor já na regra certa. `encodeURIComponent` porque a chave carrega `:` e o
   * nome do grupo do ERP (que pode ter espaço, `&`, acento).
   */
  const editAmpliRule = (ruleKey) => {
    if (!activeProject?._id || !ruleKey) return;
    navigate(
      `/dashboard/adiantamentos/${activeProject._id}?editRule=${encodeURIComponent(ruleKey)}`,
    );
  };
  const [resources, setResources] = useState({ items: [], totalPages: 1 })
  const [toPay, setToPay] = useState(false)
  const [tabView, setTabView] = useState('resources')
  const megaContractsTab = tabView === 'contracts' && megaProject;
  const [seeEAP, setSeeEAP] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [loading, setLoading] = useState(false)
  const [creditorOnly, setCreditorOnly] = useState(false)
  const [editingTitle, setEditingTitle] = useState(null);
  const [extraValue, setExtraValue] = useState({
    value: 0,
    description: ''
  })
  const [updatedExtra, setUpdatedExtra] = useState("")
  const [updatedStock, setUpdatedStock] = useState("")
  const [stock, setStock] = useState({
    value: 0,
    description: '',
    reference_date: ''
  })
  const [resourceOrder, setResourceOrder] = useState('evaluated')
  const [resourceToSearch, setResourceToSearch] = useState('')
  const [selectedDocuments, setSelectedDocuments] = useState([])
  const [uniqueDocs, setUniqueDocs] = useState([])
  const [stockType, setStockType] = useState('api')
  const [resourceGroupItemCount, setResourceGroupItemCount] = useState(null)
  const [resourceGroupAiOpen, setResourceGroupAiOpen] = useState(false)
  // Painel mensal (aba Planejado x medido): mês em foco e legenda sobrevivem à ida e volta
  // da investigação; `investigation` fixa item + mês + natureza na aba Fornecedores.
  const [monthlyFocusMonth, setMonthlyFocusMonth] = useState(null)
  const [monthlyHiddenSeries, setMonthlyHiddenSeries] = useState([])
  const [investigation, setInvestigation] = useState(null)
  const resourceGroupAiLauncherRef = useRef(null)
  const restoreResourceGroupAiFocusRef = useRef(false)
  const resourceLoadRef = useRef(null)
  const viewKey = presentationViewKey(location.pathname, activeProject)
  const previousViewKey = useRef(viewKey)
  const pathname = location.pathname
  const isFinancialProjection = pathname.includes('projecao-financeira')
  const isValueAdded = VALUE_ADDED_PATHS.some((path) => (
    matchPath({ path, end: true }, pathname)
  ))
  const isPlannedMeasured = tabView === 'plannedMeasured'
  const isGraphView = tabView === 'graph' || isPlannedMeasured
  const restrictedBillsTab = tabView === 'bills';
  const resourceScope = {
    workId: String(activeProject?._id ?? ''),
    wbs: String(item?.wbs_code_id ?? ''),
  }
  if (
    !resourceLoadRef.current ||
    resourceLoadRef.current.workId !== resourceScope.workId ||
    resourceLoadRef.current.wbs !== resourceScope.wbs
  ) {
    resourceLoadRef.current = {
      ...resourceScope,
      sequence: (resourceLoadRef.current?.sequence ?? 0) + 1,
    }
  }
  const presentationReadOnly = isPresentationProject(activeProject)
  const canOfferResourceGroupAi = (
    isValueAdded &&
    tabView === 'resources' &&
    !presentationReadOnly &&
    Boolean(activeProject?._id) &&
    Boolean(item?.wbs_code_id) &&
    resourceGroupItemCount > 0
  )

  // Orçamento base da obra zerado (0/null/undefined): explica o porquê da lista vazia.
  const isZeroBudget = !activeProject?.budget?.value
  const beginResourceLoad = () => {
    const sequence = resourceLoadRef.current.sequence + 1
    const token = { ...resourceScope, sequence }
    resourceLoadRef.current = token
    return token
  }
  const isCurrentResourceLoad = (token) => (
    resourceLoadRef.current.workId === token.workId &&
    resourceLoadRef.current.wbs === token.wbs &&
    resourceLoadRef.current.sequence === token.sequence
  )
  const commitResourceLoad = (token, write) => {
    if (!isCurrentResourceLoad(token)) return false
    write()
    return true
  }
  const invalidateResourceLoads = () => {
    resourceLoadRef.current = {
      ...resourceLoadRef.current,
      sequence: resourceLoadRef.current.sequence + 1,
    }
  }
  useEffect(() => {
    if (previousViewKey.current !== viewKey) {
      closeItemDetailOffcanvas()
      selectDetail(null, null)
      setResources({ items: [], totalPages: 1, itemsCount: 0 })
      setUpdatedExtra('')
      setUpdatedStock('')
      setResourceToSearch('')
      setSelectedDocuments([])
      setUniqueDocs([])
    }
    previousViewKey.current = viewKey
  }, [viewKey])
  useEffect(() => {
    setResourceGroupAiOpen(false)
    setResourceGroupItemCount(null)
    restoreResourceGroupAiFocusRef.current = false
  }, [
    activeProject?._id,
    item?.wbs_code_id,
    pathname,
    presentationReadOnly,
    showItemDetailOffcanvas,
  ])
  useEffect(() => {
    setMonthlyFocusMonth(null)
    setMonthlyHiddenSeries([])
    setInvestigation(null)
  }, [activeProject?._id, item?.wbs_code_id, pathname])

  useEffect(() => {
    if (tabView !== 'creditors') setInvestigation(null)
  }, [tabView])

  useEffect(() => {
    if (isValueAdded && tabView === 'resources') return
    setResourceGroupAiOpen(false)
    restoreResourceGroupAiFocusRef.current = false
  }, [isValueAdded, tabView])
  useEffect(() => {
    if (resourceGroupAiOpen || !restoreResourceGroupAiFocusRef.current || !canOfferResourceGroupAi) return
    restoreResourceGroupAiFocusRef.current = false
    resourceGroupAiLauncherRef.current?.focus({ preventScroll: true })
  }, [canOfferResourceGroupAi, resourceGroupAiOpen])

  // Consulta de fornecedores/títulos. Com investigação ativa (vinda do painel mensal), o mês
  // em foco é passado explicitamente e não pode ser sobrescrito pelo mês global da página.
  const creditorQuery = (page, pageSize, searchString, documents) => {
    if (investigation?.monthKey) {
      return [
        type,
        item?.wbs_code_id,
        page,
        monthKeyToApiMonth(investigation.monthKey),
        true,
        toPay,
        pageSize,
        searchString,
        documents,
        { explicitMonth: true },
      ]
    }
    const limitDate = window.location.pathname.includes('projecao-financeira')
    return [type, item?.wbs_code_id, page, barChartDate, limitDate, toPay, pageSize, searchString, documents]
  }

  const monthlyWindow = useMemo(() => ({
    beginAt: offCanvasFilterDates?.beginAt,
    endAt: offCanvasFilterDates?.endAt,
  }), [offCanvasFilterDates?.beginAt, offCanvasFilterDates?.endAt])

  // Os cinco arrays mensais do contrato atual. `scheduleCurves` (acumulado) fica de fora.
  const monthlySeries = useMemo(() => ({
    baseline: graphData?.baseline,
    predicted: graphData?.predicted,
    measured: graphData?.measured,
    paidValues: graphData?.paidValues,
    toPayValues: graphData?.toPayValues,
  }), [graphData?.baseline, graphData?.predicted, graphData?.measured, graphData?.paidValues, graphData?.toPayValues])

  // Resposta de outro período (ou ainda sem resposta) não é exibida como se fosse a atual.
  const monthlyWindowMatches = (
    !graphData?.bgn || !graphData?.nd || !monthlyWindow.beginAt || !monthlyWindow.endAt
      ? Boolean(graphData?.bgn && graphData?.nd)
      : monthKeyFromDate(graphData.bgn) === monthKeyFromDate(monthlyWindow.beginAt)
        && monthKeyFromDate(graphData.nd) === monthKeyFromDate(monthlyWindow.endAt)
  )
  const monthlyLoading = Boolean(scheduleAnalysis?.loading) || (!scheduleAnalysis?.error && !monthlyWindowMatches)

  const monthlyFocus = monthlyFocusMonth ?? defaultFocusMonth({
    monthKeys: monthlyMonthKeys(monthlyWindow),
    preferred: (projectionTable && tableMonthSelected) ? tableMonthSelected : barChartDate,
  })

  const monthlyInvestigations = {
    activities: scheduleAnalysis?.data?.available
      ? {
          available: true,
          reference: {
            syncedAt: scheduleAnalysis.data.syncedAt,
            paginated: (scheduleAnalysis.data.pagination?.totalPages ?? 1) > 1,
          },
        }
      : { available: false, reason: "A análise de atividades não está disponível para este item." },
    paid: (!isBuildingTrial() && item?.wbs_code_id && type)
      ? { available: true }
      : { available: false, reason: "A lista de fornecedores não está disponível para este item nesta conta." },
    toPay: (!isBuildingTrial() && item?.wbs_code_id && type)
      ? { available: true }
      : { available: false, reason: "A lista de títulos a pagar não está disponível para este item nesta conta." },
  }

  const toggleMonthlySeries = (key) => setMonthlyHiddenSeries((current) => (
    current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key]
  ))

  const filterMonthlyMonth = (monthKey) => {
    const monthStart = monthKeyToDate(monthKey)
    if (!monthStart || !item?.wbs_code_id) return
    setMonthlyFocusMonth(monthKey)
    changeGraphDates(item.wbs_code_id, monthStart, monthStart, { refreshGraph: false })
  }

  const investigateMonthly = (kind, { monthKey } = {}) => {
    if (!monthKey) return
    if (kind === 'activities') {
      const anchor = document.getElementById('planned-measured-activities')
      anchor?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
      anchor?.focus?.({ preventScroll: true })
      return
    }
    setInvestigation({ kind, monthKey, wbs: item?.wbs_code_id, type })
    setToPay(kind === 'toPay')
    setCurrentPage(1)
    setResourceToSearch('')
    setTabView('creditors')
  }

  const returnToMonthlyComparison = () => {
    setInvestigation(null)
    setTabView('plannedMeasured')
  }

  const emptyMessages = {
    creditors: "Nenhum fornecedor encontrado para este item.",
    resources: "Nenhum insumo encontrado para este item.",
    contracts: "Nenhum contrato encontrado para este item.",
    stock: "Nenhum item de estoque encontrado para este item."
  };
  const filterResources = async () => {
    if (isGraphView) {
      invalidateResourceLoads()
      setLoading(false)
      setResources({ items: [], totalPages: 0, itemsCount: 0 })
      return
    }
    const token = beginResourceLoad()
    commitResourceLoad(token, () => {
      setLoading(true)
      setResources((current) => ({ ...current, items: [] }))
    })
    if (restrictedBillsTab || megaContractsTab) {
      commitResourceLoad(token, () => {
        setResources({ items: [], totalPages: 0, itemsCount: 0 })
        setLoading(false)
      })
      return
    }
    try {
      let response;
      if (tabView === 'creditors') {
        response = await getCreditors(...creditorQuery(currentPage, 20, "", selectedDocuments))
      } else if (tabView === 'contracts') {
        response = await getContracts(type, item?.wbs_code_id, 'true')
      } else if (tabView === 'stock') {
        response = await getStock(item?.wbs_code_id, stockType, resourceToSearch)
      } else {
        response = await getResources(item?.wbs_code_id, currentPage, resourceOrder, 20, '')
      }
      commitResourceLoad(token, () => {
        if (tabView === 'creditors') setUniqueDocs(response?.uniqueDocuments)
        if (tabView === 'resources') {
          const pageItemCount = Array.isArray(response?.items) ? response.items.length : 0
          const responseTotal = response?.totalItemsCount == null
            ? Number.NaN
            : Number(response.totalItemsCount)
          setResourceGroupItemCount(
            Number.isFinite(responseTotal)
              ? Math.max(0, responseTotal, pageItemCount)
              : pageItemCount,
          )
        }
        setResources({
          items: response?.items,
          totalPages: response?.totalPages,
          itemsCount: response?.totalItemsCount,
          annulled: tabView === 'creditors' ? response?.annulled : undefined,
        })
      })
    } catch (_error) {
      commitResourceLoad(token, () => {
        if (tabView === 'resources') setResourceGroupItemCount(null)
        setResources({ items: [], totalPages: 0, itemsCount: 0 })
      })
    } finally {
      commitResourceLoad(token, () => setLoading(false))
    }
  };
  useEffect(() => {
    setCurrentPage(1)
    setExtraValue({ value: item?.extraValue?.value ?? 0, description: item?.extraValue?.description ?? "" })
    setStock({
      value: item?.stock?.value ?? 0,
      description: item?.stock?.description ?? "",
      reference_date: item?.stock?.reference_date ? String(item.stock.reference_date).slice(0, 10) : "",
    })
  }, [type, item, tabView, toPay, resourceOrder])
  useEffect(() => {
    if (!showItemDetailOffcanvas) {
      invalidateResourceLoads()
      setLoading(false)
      setResources({ items: [], totalPages: 1, itemsCount: 0 })
      return
    }
    filterResources()
    return () => {
      invalidateResourceLoads()
      setResources({ items: [], totalPages: 1, itemsCount: 0 })
      setUpdatedExtra('')
      setUpdatedStock('')
    }
  }, [
    activeProject?._id,
    type,
    item,
    item?.wbs_code_id,
    currentPage,
    tabView,
    toPay,
    resourceOrder, tableMonthSelected,
    stockType, showItemDetailOffcanvas]);
  useEffect(() => {
    if (isFinancialProjection) {
      setCreditorOnly(true)
      setTabView(defaultItemDetailTab({
        isFinancialProjection,
        hasPrevision: activeProject?.hasPrevision,
      }))
    } if (pathname.includes('insumos') || pathname.includes('curva')) {
      setSeeEAP(true)
      setCreditorOnly(true)
    }
    return () => {
      setSeeEAP(false)
      setCreditorOnly(false)
    }
  }, [
    pathname,
    isFinancialProjection,
    activeProject?._id,
    activeProject?.hasPrevision,
    item?.wbs_code_id,
    showItemDetailOffcanvas,
  ])
  useEffect(() => {
    if (!isGraphView || isPlannedMeasured || !item?.wbs_code_id) return
    getSingleFollowUps(item.wbs_code_id)
  }, [isGraphView, isPlannedMeasured, item?.wbs_code_id])
  const goBack = () => {
    if (currentPage === 1) return;

    setCurrentPage(prev => prev - 1)
  }
  const goForward = () => {
    if (currentPage === resources.totalPages) return;

    setCurrentPage(prev => prev + 1)
  }
  const typeTitle = useMemo(() => {
    switch (type) {
      case "unit":
        return "Unidade Construtiva";
      case "cell":
        return "Célula Construtiva";
      case "step":
        return "Etapa";
      case "substep":
        return "Subetapa";
      case "service":
        return "Serviço";
      case "resource":
        return "Insumo";
      default:
        return "";
    }
  }, [type])
  if (!item) return null;

  const updateLiveValues = (field, canonicalValue) => {
    const newVal = canonicalValue ?? (field === 'stock' ? stock.value : extraValue.value)
    const newValue = typeof newVal === 'string'
      ? Number(newVal.replaceAll(".", "").replace(",", "."))
      : Number(newVal)
    const arrayName = `${type}s`;
    let newArray = [];
    if (type !== 'unit') {
      const oldArray = activeProject[arrayName];
      newArray = Object.fromEntries(
        Object.entries(oldArray).map(([groupKey, array]) => [
          groupKey,
          array.map((i) =>
            i._id === item._id
              ? { ...i, [field]: { ...i[field], value: newValue } }
              : i
          ),
        ])
      );
    } else {
      newArray = activeProject[arrayName].map((i) =>
        i._id === item._id
          ? { ...i, [field]: { ...i[field], value: newValue } }
          : i
      );
    }
    const newProjItem = { ...activeProject, [arrayName]: newArray };
    updateProjectLive(newProjItem);
  };


  const handleUpdateExtra = async () => {
    const rawValue = typeof extraValue.value === "string"
      ? extraValue.value.replaceAll(".", "").replace(",", ".")
      : extraValue.value;
    const update = await updateExtra(
      item.wbs_code_id,
      itemDetailExtraValueMutation(Number(rawValue), extraValue.description),
      item.description,
    )
    if (update.code === 200) {
      setUpdatedExtra(200)
      const canonicalDocument = update.document ?? (
        await getExtraValues(activeProject.building_id, item.wbs_code_id)
      )?.response?.[0]
      const canonicalTotal = extraValueDocumentTotal(canonicalDocument)
      if (canonicalTotal !== null) updateLiveValues('extraValue', canonicalTotal)
    } else {
      setUpdatedExtra(500)
    }
  }
  const handleUpdateStock = async () => {
    const update = await updateStock(
      item.wbs_code_id,
      Number(stock.value.replaceAll(".", "").replace(",", ".")),
      stock.description,
      item.description,
      // Ausência de data e data mal-formada são tratadas de forma diferente no servidor de
      // propósito — nunca substituir por "hoje" no cliente.
      stock.reference_date || undefined
    )
    if (update.code === 200) {
      setUpdatedStock(200)
      updateLiveValues('stock')
    } else {
      // 400 (data inválida) e 403 (nó fora da empresa do usuário) vêm com `message`
      // explicando o motivo — mostrar em vez do genérico "erro interno" de sempre.
      setUpdatedStock(update.message || "Erro interno, tente novamente em breve.")
    }
  }
  const handleResourceOrder = (e) => {
    e.preventDefault()
    setResourceOrder(e.target.value)
  }
  const closeResourceGroupAi = () => {
    restoreResourceGroupAiFocusRef.current = true
    setResourceGroupAiOpen(false)
  }
  const calcStock = () => {

    const newVal = item.paid - item.measured;
    const calcText = newVal.toFixed(2).toString()

    let [integerPart, decimalPart] = calcText.split(".");

    if (integerPart) {
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }

    setStock({ ...updateExtra, value: `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${decimalPart}` })
  }
  const checkName = () => {
    if (item.description) {
      if (window.location.pathname.includes('projecao-financeira') && projectionTable) {
        return `${item.description} - ${tableMonthSelected}`
      } else {
        return item.description
      }
    }
    if (item.building_unit_name) {
      if (window.location.pathname.includes('projecao-financeira') && projectionTable) {
        return `${item.building_unit_name} - ${tableMonthSelected}`
      } else {
        return item.building_unit_name
      }
    }
    if (item.name) return item.name
  }
  const handleDownloadResource = async e => {
    let allRes;
    let res = []
    let fields = []
    if (tabView === 'resources') {
      allRes = await getResources(item?.wbs_code_id, currentPage, resourceOrder, 999999, "")
      allRes.items.forEach(e => {
        if (e.attended_items.length > 0) {
          e.attended_items.forEach(i => {
            res.push({
              id: e.id,
              name: e.name,
              wbs: i.wbs_code,
              measurement_unit: e.measurement_unit,
              attended_quantity: i.quantity,
              attended_value: i.value,
              attended_total: i.total,
              attended_indexed: i.indexed_value,
            })
          })
        }
        if (e.evaluated_items.length > 0) {
          e.evaluated_items.forEach(i => {
            res.push({
              id: e.id,
              name: e.name,
              wbs: i.wbs_code,
              measurement_unit: e.measurement_unit,
              evaluated_quantity: i.quantity,
              evaluated_value: i.value,
              evaluated_total: i.total,
              evaluated_indexed: i.indexed_value,
            })
          })
        }
      });
      fields = [
        { label: 'ID', key: 'id' },
        { label: 'Nome', key: 'name' },
        { label: 'WBS', key: 'wbs' },
        { label: 'Unidade de medida', key: 'measurement_unit' },
        { label: 'Quantidade Orçada', key: 'evaluated_quantity' },
        { label: 'Valor Orçado', key: 'evaluated_value' },
        { label: 'Total Orçado', key: 'evaluated_total' },
        { label: 'Orçado Indexado', key: 'evaluated_indexed' },
        { label: 'Quantidade Apropriada', key: 'attended_quantity' },
        { label: 'Valor Apropriado', key: 'attended_value' },
        { label: 'Total Apropriado', key: 'attended_total' },
        { label: 'Apropriado Indexado', key: 'attended_indexed' },
      ]
    } else if (tabView === 'creditors') {
      allRes = await getCreditors(...creditorQuery(currentPage, 9999999, "", selectedDocuments))

      allRes.items.forEach(i => {
        i.items.forEach(j => {
          j.items.forEach(k => {
            res.push({
              creditor_name: k.creditor_name,
              document: k.document,
              date: k.date,
              id: titleIdentifier(k),
              value: k.value,
              note: k.note || ""
            })
          })
        })
      })

      fields = [
        { label: 'Fornecedor', key: 'creditor_name' },
        { label: 'Documento', key: 'document' },
        { label: 'Título', key: 'id' },
        { label: 'Data', key: 'date' },
        { label: 'Valor', key: 'value' },
        { label: 'Observação', key: 'note' },
      ]
    } else if (tabView === 'contracts') {

      resources.items.forEach(i => {
        i.items.forEach(j => {
          j.items.forEach((k) => {
            res.push({
              contract_number: k.contract_number,
              supplier_name: k.supplier_name,
              description: k.description,
              total_labor_value: k.total_labor_value,
              quantity: k.quantity,
              measured_value: k.measured_value
            })
          })
        })
      })

      fields = [
        { label: 'Número do contrato', key: 'contract_number' },
        { label: 'Fornecedor', key: 'supplier_name' },
        { label: 'Descrição', key: 'description' },
        { label: 'Quantidade', key: 'quantity' },
        { label: 'Valor medido', key: 'measured_value' },
        { label: 'Valor total', key: 'total_labor_value' },
      ]
    } else if (tabView === "stock") {
      fields = [
        { label: 'WBS', key: 'wbs_code_id' },
        { label: 'Item', key: 'resource_name' },
        { label: 'Descrição', key: 'detail_description' },
        { label: 'Valor', key: 'stock_total' },
      ]

      resources.items.forEach(({ wbs_code_id, resource_name, detail_description, stock_total }) => {
        res.push({
          wbs_code_id,
          resource_name,
          detail_description,
          stock_total
        })
      })
    }

    trackReportDownload(`detalhe_item_${tabView}`, { format: "xlsx", wbs: item?.wbs_code_id });
    downloadCsv(e, fields, res)
  };
  const handleSearchResource = async e => {
    e.preventDefault()
    if (restrictedBillsTab) return
    if (resourceToSearch === '') {
      setPerPage(20)
      await filterResources()
      return
    }
    const token = beginResourceLoad()
    commitResourceLoad(token, () => {
      setLoading(true)
      setResources((current) => ({ ...current, items: [] }))
    })
    let res;
    try {
      if (tabView === 'creditors') {
        res = await getCreditors(...creditorQuery(currentPage, 9999999, resourceToSearch, selectedDocuments))
      } else if (tabView === 'stock') {
        res = await getStock(item?.wbs_code_id, stockType, resourceToSearch)
      } else {
        res = await getResources(item?.wbs_code_id, 1, resourceOrder, 999999, resourceToSearch)
      }
      commitResourceLoad(token, () => {
        if (tabView === 'creditors') setUniqueDocs(res?.uniqueDocuments)
        setPerPage(res?.items?.length ?? 20)
        setResources({
          items: res?.items,
          totalPages: res?.totalPages,
          itemsCount: res?.totalItemsCount,
          annulled: tabView === 'creditors' ? res?.annulled : undefined,
        })
      })
    } catch (_error) {
      commitResourceLoad(token, () => {
        setPerPage(20)
        setResources({ items: [], totalPages: 0, itemsCount: 0 })
      })
    } finally {
      commitResourceLoad(token, () => setLoading(false))
    }
  };
  return <>
    <div className={showItemDetailOffcanvas ? "offcanvas active" : "offcanvas"}>
      <div className="offcanvas-container large">
        <hgroup className="offcanvas-header">
          <h2>{`${typeTitle} - ${checkName()}`}</h2>
          <button onClick={toggleItemDetailOffcanvas}>
            <i
              className="icon icon-width-24 icon-times background-accent"
              aria-hidden={true}
            ></i>
          </button>
        </hgroup>
        <div className="offcanvas-content">
          {seeEAP ?
            <>
              <div className="grid gap-8 columns-2-tablet columns-2-laptop-l">
                {resourceEAP?.response && <EapLevels response={resourceEAP.response} />}
                <EapDisb item={item} resourceEAP={resourceEAP} />
              </div>
              <div>
                {resourceEAP?.response &&
                  <EapResources
                    item={item}
                    appropriation_level={activeProject.appropriation_level}
                    divisions={resourceEAP.response}
                  />}
              </div>
            </> : <>
              {!isBuildingTrial() && !projectionTable && window.location.pathname.includes('projecao-financeira') && <MainCards item={item} />}

            </>}
          {!seeEAP && <>
            {type === activeProject.appropriation_level ?
              <>
                <PresentationMutationGate project={activeProject}>
                  <OffcanvasLessons />
                  {(projectionTable && window.location.pathname.includes('projecao-financeira')) && <>
                    <TableMonthExtraValues />
                    {ampliMode ? (
                      <AmpliMonthDisbursementSection
                        item={item}
                        competence={monthCompetence}
                        projectId={activeProject._id}
                        readOnly={isPresentationProject(activeProject)}
                        annulments={activeProject?.titleAnnulments}
                        onAdjusted={() => selectProject?.(activeProject._id)}
                        onEditRule={editAmpliRule}
                      />
                    ) : (
                      <TableMonthAdvancements />
                    )}
                  </>}
                  <UpdateValues
                    extraValue={extraValue}
                    setExtraValue={setExtraValue}
                    updatedExtra={updatedExtra}
                    handleUpdateStock={handleUpdateStock}
                    handleUpdateExtra={handleUpdateExtra}
                    updatedStock={updatedStock}
                    stock={stock}
                    setStock={setStock}
                    calcStock={calcStock}
                  />
                </PresentationMutationGate>
              </> : <AppropriationLevelWarning />

            }
            <div className="offcanvas-tabs">
              {isFinancialProjection && activeProject?.hasPrevision && (
                <button
                  onClick={() => setTabView('plannedMeasured')}
                  className={isPlannedMeasured ? 'active' : undefined}
                >
                  Planejado x medido
                </button>
              )}
              {!isBuildingTrial() && <button onClick={() => setTabView('creditors')} className={tabView === 'creditors' ? 'active' : undefined}>Fornecedores</button>}
              {!creditorOnly && <button onClick={() => setTabView('resources')} className={tabView === 'resources' ? 'active' : undefined}>Insumos</button>}
              {!creditorOnly && <button onClick={() => setTabView('contracts')} className={tabView === 'contracts' ? 'active' : undefined}>Contratos</button>}
              {!creditorOnly && <button onClick={() => setTabView('stock')} className={tabView === 'stock' ? 'active' : undefined}>Estoque</button>}

              {creditorOnly && !activeProject?.hasPrevision && <button onClick={() => setTabView('graph')} className={tabView === 'graph' ? 'active' : undefined}>Gráfico</button>}

            </div>
            {canOfferResourceGroupAi && (
              <div className="resource-group-ai-entry margin-top-8">
                {resourceGroupAiOpen ? (
                  <div id="resource-group-ai-panel">
                    <ResourceGroupAiPanel
                      workId={activeProject._id}
                      contextId={item.wbs_code_id}
                      groupName={`${typeTitle} · ${checkName()}`}
                      project={activeProject}
                      onClose={closeResourceGroupAi}
                      preserveScrollOnFocus
                    />
                  </div>
                ) : (
                  <button
                    ref={resourceGroupAiLauncherRef}
                    type="button"
                    className="resource-group-ai-action"
                    aria-controls="resource-group-ai-panel"
                    aria-expanded="false"
                    onClick={() => setResourceGroupAiOpen(true)}
                  >
                    Analisar grupo de insumos com IA
                  </button>
                )}
              </div>
            )}
            {tabView === 'creditors' && investigation?.monthKey && (
              <div className="schedule-callout monthly-investigation-banner" role="status" aria-label="Contexto da investigação">
                <p>
                  Investigando <strong>{toPay ? 'A pagar' : 'Pago'}</strong> de <strong>{formatMonthLabel(investigation.monthKey)}</strong>
                  {' · item '}<strong>{investigation.wbs}</strong>{typeTitle ? ` · nível ${typeTitle}` : ''}.
                  {toPay
                    ? ' Saldo aberto pelo mês de vencimento, na posição da última carga.'
                    : ' Títulos pelo mês de movimento financeiro.'}
                  {' '}Paginação e busca não alteram o gráfico.
                </p>
                <button type="button" className="button button-accent small" onClick={returnToMonthlyComparison}>
                  Voltar à comparação
                </button>
              </div>
            )}
            <div className="flex align-center justify-content-space-between gap-16 margin-top-8">
              {!restrictedBillsTab && (tabView === 'resources' || tabView === 'creditors' || tabView === 'stock') ?
                <form className="flex gap-8 align-center" onSubmit={handleSearchResource}>
                  {tabView === 'creditors' && <div style={{ width: '300px', display: 'flex', alignItems: 'center' }}>
                    <Select
                      style={{ width: '100%' }}
                      isMulti
                      isClearable={false}
                      name="creditors"
                      options={uniqueDocs?.map(i => ({ value: i, label: i }))}
                      onChange={(e) => setSelectedDocuments(e.map(i => i.value))}
                      className="basic-multi-select full-width"
                      classNamePrefix="select"
                      placeholder='Filtrar documentos'
                    />
                  </div>}
                  <input
                    className="resource-search"
                    aria-label={RESOURCE_SEARCH_LABELS[tabView] ?? "Pesquisar neste grupo"}
                    value={resourceToSearch}
                    onChange={(e) => setResourceToSearch(e.target.value)}
                  />
                  <button className="button button-accent small" onClick={handleSearchResource}>Pesquisar</button>
                </form>
                : <div />
              }
              {!isGraphView && !restrictedBillsTab && <button className="button button-accent small" onClick={handleDownloadResource}>Baixar relatório</button>}
            </div>
            <div className="flex space-between gap-16 margin-top-8 offcanvas-buttons">
              {tabView === 'creditors' &&
                <div className="flex gap-8">
                  <button onClick={() => setToPay(false)} className={!toPay ? 'active' : undefined}>Pago</button>
                  <AnnulmentIndicator summary={activeProject?.titleAnnulments} kind="paid" wbsCode={item?.wbs_code_id ?? ""} />
                  <button onClick={() => setToPay(true)} className={toPay ? 'active' : undefined}>A pagar</button>
                  <AnnulmentIndicator summary={activeProject?.titleAnnulments} kind="toPay" wbsCode={item?.wbs_code_id ?? ""} />
                </div>
              }

              {tabView === 'stock' &&
                <div className="flex gap-8">
                  <button onClick={() => setStockType('api')} className={stockType === "api" ? 'active' : undefined}>Sienge</button>
                  <button onClick={() => setStockType('ampli')} className={stockType === "ampli" ? 'active' : undefined}>Ampli</button>
                </div>
              }
              {tabView === 'resources' &&
                <div className="flex gap-8 align-center">
                  <p className="margin-bottom-8">Ordenar por: </p>
                  <select
                    aria-label="Ordenar insumos por"
                    value={resourceOrder}
                    onChange={handleResourceOrder}
                  >
                    <option value='evaluated'>Total Orçado</option>
                    <option value='attended'>Total Apropriado</option>
                  </select>
                </div>
              }
              {(!loading && tabView !== 'contracts' && !isGraphView && tabView !== "stock") &&
                <Pagination count={resources.itemsCount} perPage={perPage} currentPage={currentPage} goBack={goBack} goForward={goForward} />
              }
            </div>
          </>}

          {isPlannedMeasured ? <PlannedMeasuredPanel
              wbs={item.wbs_code_id}
              scheduleState={scheduleAnalysis}
              sourceMetadata={graphData?.scheduleSource}
              onRequest={getScheduleAnalysis}
              beginAt={offCanvasFilterDates?.beginAt}
              endAt={offCanvasFilterDates?.endAt}
              graph={(
                <>
                  <OffCanvasDateFilter refreshGraph={false} />
                  <MonthlyProjectionComparison
                    series={monthlySeries}
                    window={monthlyWindow}
                    loading={monthlyLoading}
                    error={scheduleAnalysis?.error ?? null}
                    item={{
                      wbs_code_id: item.wbs_code_id,
                      wbs: item.wbs,
                      description: checkName(),
                      type,
                      typeLabel: typeTitle,
                    }}
                    appropriationLevel={activeProject?.appropriation_level}
                    sourceMetadata={graphData?.scheduleSource}
                    measurementReference={scheduleAnalysis?.data?.syncedAt
                      ? new Date(scheduleAnalysis.data.syncedAt).toLocaleString("pt-BR")
                      : null}
                    focusMonth={monthlyFocus}
                    onFocusMonth={setMonthlyFocusMonth}
                    hiddenSeries={monthlyHiddenSeries}
                    onToggleSeries={toggleMonthlySeries}
                    onFilterMonth={filterMonthlyMonth}
                    investigations={monthlyInvestigations}
                    onInvestigate={investigateMonthly}
                  />
                </>
              )}
            /> : tabView === 'graph' ? <div style={{ height: '450px' }}>
              <OffCanvasDateFilter />
              <OffCanvasGraph />
            </div> :
            <div className="accordion margin-top-24">
              {resources?.items?.length > 0 ? resources?.items.map((i, idx) => {
                  switch (tabView) {
                    case 'creditors':
                      return <CreditorAccordion
                        key={i._id || i.id || i.creditor_id || `${i.creditor_name || 'creditor'}-${i.document_number || idx}`}
                        item={i}
                        writebackEnabled={activeProject?.writebackEnabled}
                        onEditAppropriation={presentationReadOnly ? null : setEditingTitle}
                      />
                case 'resources':
                      return <ResourceAccordion
                        key={i.id ?? idx}
                        resource={i}
                        index={idx}
                      />
                case 'contracts':
                      return <ContractAccordion key={i._id || i.id || i.contract_number || idx} item={i} />
                case 'stock':
                      return <StockAccordion key={i._id || i.id || i.resource_id || idx} item={i} />
                default:
                      break;
                  }
                }) : !loading && (
                  megaContractsTab
                    ? <p className="empty-state">{MEGA_CONTRACTS_WITHOUT_BUDGET_LINK}</p>
                    : isZeroBudget
                    ? <ZeroBudgetNotice />
                    : <p className="empty-state">{emptyMessages[tabView] ?? "Nada encontrado para este item."}</p>
                )}
            </div>}

          {/* Títulos anulados na Ampli: seção à parte, fora de qualquer total da lista. */}
          {tabView === 'creditors' && !loading && <AnnulledCreditorsSection annulled={resources?.annulled} />}

          {loading && <div className="margin-top-24"><LoaderItem /></div>}
        </div>
      </div>
      {showItemDetailOffcanvas && <div onClick={toggleItemDetailOffcanvas} className="offcanvas-background" />}
    </div>
    <AppropriationEditor
      open={!presentationReadOnly && !!editingTitle}
      projectId={activeProject?._id}
      billId={editingTitle?.billId}
      titleInfo={editingTitle}
      onClose={() => setEditingTitle(null)}
      // Veio de um item da EAP: se estamos no nível de apropriação da obra, pré-preenche o
      // rateio nesse item (building_unit_id + wbs = costEstimationSheetId).
      prefill={
        type === activeProject?.appropriation_level && item?.wbs && item?.building_unit_id != null
          ? {
              buildingUnitId: item.building_unit_id,
              costEstimationSheetId: item.wbs,
              description: item.description,
            }
          : null
      }
      // Recarrega a lista de fornecedores/títulos após salvar para refletir o novo rateio.
      onSaved={filterResources}
    />
    </>;
};

export default ItemDetailOffcanvas;
